import { z } from "zod";
import { TranslationMapSchema } from "./utils";
import {
  AnchorResponseSchema,
  OptionResponseSchema,
  questionTypeSchema,
} from "./surveys";

/**
 * Survey answer submission — the mobile app submits a survey's answers
 * all-at-once when the patient presses "Send" (the local draft is the
 * only storage before that, and is cleared after the server confirms).
 *
 * Submission is immutable and idempotent per OCCURRENCE, meaning
 * (enrollment, scheduled survey, occurrence key): the first submission
 * creates the record (201); any resubmission, a network retry or a double
 * tap, returns the existing receipt (200) without modifying it.
 *
 * The occurrence is part of the identity because the same survey can be due
 * many times in one study. It is also what the server checks a submission
 * against: answers to an occurrence that has not opened, or whose window has
 * closed, are refused rather than filed under the nearest one.
 */

export const SubmitSurveyAnswerSchema = z.discriminatedUnion("type", [
  z.object({
    questionId: z.string(),
    type: z.literal("SINGLE"),
    optionIds: z.array(z.string()).length(1),
  }),
  z.object({
    questionId: z.string(),
    type: z.literal("MULTIPLE"),
    optionIds: z.array(z.string()).min(1),
  }),
  /**
   * A RANGE answer is EITHER a point on the scale or one of the question's
   * secondary options, never both and never neither.
   *
   * Two fields rather than two union members, because the union is
   * discriminated on `type` and cannot hold two `"RANGE"` branches. Which one
   * is populated is checked server-side alongside every other answer rule
   * (`assertAnswersMatchSurvey`), where the question is in hand: a client
   * sending both is not saying something ambiguous, it is disagreeing with the
   * questionnaire.
   *
   * Off-scale codes stay OUT of `value` deliberately. "Not applicable" is not a
   * large measurement, and storing 9 in the same field as a 1-to-7 answer would
   * make it one to every reader that has not been told otherwise: the analysis
   * payload, a mean, an export, a `gte` condition.
   */
  z.object({
    questionId: z.string(),
    type: z.literal("RANGE"),
    /** The point on the scale, or null when a secondary option was chosen. */
    value: z.number().int().nullable(),
    /** At most one, and only one of the question's `secondaryOptions`. */
    optionIds: z.array(z.string()).max(1).default([]),
  }),
]);
export type SubmitSurveyAnswer = z.infer<typeof SubmitSurveyAnswerSchema>;

export const SubmitSurveyResponseInputSchema = z.object({
  answers: z.array(SubmitSurveyAnswerSchema).min(1),
});
export type SubmitSurveyResponseInput = z.infer<
  typeof SubmitSurveyResponseInputSchema
>;

/** What the client gets back — enough to mark the survey completed. */
export const SurveyResponseReceiptSchema = z.object({
  id: z.string(),
  /**
   * The enrolment this answers, or NULL for routine monitoring, which has no
   * enrolment because it has no study. Kept on the receipt because a study's
   * client uses it, and nullable rather than absent because one submission
   * route now serves both kinds of owner.
   */
  patientStudyId: z.string().nullable(),
  scheduledSurveyId: z.string(),
  surveyId: z.string(),
  /** Which occurrence this answers. Opaque to the client. */
  occurrenceKey: z.string(),
  submittedAt: z.coerce.date(),
});
export type SurveyResponseReceipt = z.infer<typeof SurveyResponseReceiptSchema>;

/**
 * No `studyId`, deliberately (2026-08-11).
 *
 * A submission is keyed on (patient, schedule, occurrence) and a schedule need
 * not belong to a study at all, so naming one in the URL would have meant a
 * second route and a second answering flow in the patient app for routine
 * monitoring. The schedule already knows its owner; the server resolves it and
 * applies that owner's guards.
 */
export const SubmitSurveyResponseParamsSchema = z.object({
  patientId: z.string(),
  scheduledSurveyId: z.string(),
  occurrenceKey: z.string(),
});
export type SubmitSurveyResponseParams = z.infer<
  typeof SubmitSurveyResponseParamsSchema
>;

export const submitSurveyResponseSchemas = {
  params: SubmitSurveyResponseParamsSchema,
  body: SubmitSurveyResponseInputSchema,
  response: {
    201: SurveyResponseReceiptSchema,
    200: SurveyResponseReceiptSchema,
  },
};

/**
 * Read-back of a submitted response: the receipt plus the answers exactly
 * as they were submitted. Powers the read-only review screen — submitted
 * answers are served from the API, never re-persisted on the device.
 */
export const SurveyResponseDetailSchema = SurveyResponseReceiptSchema.extend({
  answers: z.array(SubmitSurveyAnswerSchema),
});
export type SurveyResponseDetail = z.infer<typeof SurveyResponseDetailSchema>;

export const getSurveyResponseSchemas = {
  params: SubmitSurveyResponseParamsSchema,
  response: { 200: SurveyResponseDetailSchema },
};

/**
 * One question as it was put to this patient, with the answer they gave.
 *
 * The question text travels WITH the answer rather than being joined on the
 * client against `GET /surveys/:id`, and that is the whole point of this shape.
 * A join would put the reader in charge of deciding which questions this
 * patient was actually asked, which is skip logic (`surveyVisibility.ts`), so a
 * client getting it wrong renders a question nobody saw as unanswered, inside a
 * document a clinician reads as a record of what was collected. The server
 * already holds exactly one answer row per question that was shown, so the
 * sheet is those rows and nothing else.
 */
export const AnsweredQuestionSchema = z.object({
  questionId: z.string(),
  /**
   * The instrument's own variable id (`PFA11`), or null on a legacy row. It is
   * how a line is matched against the printed instrument, and it is here
   * INSTEAD of an item number: the sheet holds only the questions this patient
   * was shown, so counting the lines would call item 12 item 9, and the stored
   * `position` cannot stand in either (the importer starts it at 0 while the
   * survey page numbers from 1, so the same item would be named twice over).
   */
  key: z.string().nullable(),
  text: TranslationMapSchema,
  type: questionTypeSchema,
  domain: z.object({ id: z.string(), text: TranslationMapSchema }),
  /**
   * What was chosen, in the question's own option order. One entry for a
   * SINGLE, several for a MULTIPLE, and for a RANGE either empty (the patient
   * answered on the scale) or the single secondary option they pressed instead.
   *
   * Each carries the instrument's response CODE beside its text, because
   * "which item moved" is read against the instrument's own documentation, and
   * that documentation is written in codes rather than in labels a facility may
   * have translated.
   */
  selectedOptions: z.array(OptionResponseSchema),
  /** RANGE only: the point on the scale, null when a secondary option was
   *  chosen instead. Always null on a choice question. */
  value: z.number().int().nullable(),
  /**
   * RANGE only, null on a choice question: what the number means. A bare 5 says
   * nothing until the reader knows whether the track ran 0 to 10 or 1 to 7, and
   * the anchors are the words the patient had in front of them.
   *
   * Nested rather than three sibling fields so a choice question cannot carry
   * bounds at all.
   */
  scale: z
    .object({
      minValue: z.number().int(),
      maxValue: z.number().int(),
      anchors: z.array(AnchorResponseSchema),
    })
    .nullable(),
});
export type AnsweredQuestion = z.infer<typeof AnsweredQuestionSchema>;

/**
 * One submitted occurrence, read by the DASHBOARD: what was asked, what was
 * answered, and which window it was answered in.
 *
 * Deliberately not `SurveyResponseDetailSchema`, which the patient app reads:
 * that one echoes the submission back by id, for a screen that already holds
 * the questionnaire it was filled from. A clinician opening one answer set out
 * of a patient's history holds nothing, and asking the browser to fetch a
 * 51-item questionnaire and join it is the version of this feature that decides
 * skip logic client-side.
 *
 * The window is the two stamped columns, not a re-derivation: they record the
 * window this answer was actually given in, which is what makes a late-looking
 * date explainable rather than an anomaly.
 */
export const SurveyAnswerSheetSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  scheduledSurveyId: z.string(),
  /** The schedule's name, which is what tells three attachments of one
   *  instrument apart in a study. */
  label: TranslationMapSchema,
  /** Opaque here as everywhere else: the sheet is read by its dates, and a
   *  manual trigger's key names a trigger rather than a rank. */
  occurrenceKey: z.string(),
  survey: z.object({ id: z.string(), title: TranslationMapSchema }),
  /**
   * The study this was collected for, or NULL for routine monitoring. It names
   * the owner rather than making the reader infer one from a nullable
   * enrolment id, because the two are read the same way and only their heading
   * differs.
   */
  study: z
    .object({ id: z.string(), title: TranslationMapSchema })
    .nullable(),
  submittedAt: z.coerce.date(),
  /** Null on rows written before the window was stamped at submit. */
  windowOpensAt: z.coerce.date().nullable(),
  windowClosesAt: z.coerce.date().nullable(),
  /** In the order the questionnaire asks them, and holding only the questions
   *  this patient was shown. */
  answers: z.array(AnsweredQuestionSchema),
});
export type SurveyAnswerSheet = z.infer<typeof SurveyAnswerSheetSchema>;

/**
 * Addressed by (patient, schedule, occurrence), exactly like the submission it
 * reads back, and therefore naming no study: a schedule need not belong to one.
 * One URL serves a study's questionnaire and a monitoring one, the way the
 * mobile submit route already does, and the owner decides only which gate the
 * handler applies.
 */
export const getSurveyAnswerSheetSchemas = {
  params: SubmitSurveyResponseParamsSchema,
  response: { 200: SurveyAnswerSheetSchema },
};

